import { AppMessages } from '../types';
import { pl } from './pl';

export const en: AppMessages = {
  ...pl,
  nav: { home: 'Home', study: 'Study', questionDemo: 'Question Preview', exam: 'Exam' },
  userMenu: {
    settings: 'Settings',
    status: 'Status',
    logout: 'Log out',
    anonymous: 'Signed out',
    tierFree: 'Free',
    tierPremium: 'Premium',
    tierAdmin: 'Admin',
  },
  common: { loadingSession: 'Checking session...', yes: 'Yes', no: 'No', days: 'days' },
  landing: {
    eyebrow: 'All exam categories from PLN 0.16 per day',
    title: 'Prepare for your driving license at a pace that actually helps you pass.',
    description: 'Practice official questions with videos and images, learn in short focused sessions, and check your readiness in a mock exam. Categories A, B, C, D, T and the rest are all in one place.',
    primaryCta: 'Start studying',
    secondaryCta: 'Try a mock exam',
    metricQuestions: 'catalog questions',
    metricPriceValue: 'PLN 0.16',
    metricPrice: 'per day',
    metricCategoriesValue: 'full',
    metricCategories: 'category coverage',
    heroAlt: 'Learner taking an online driving license test',
    featureOneTitle: 'Study what improves your score',
    featureOneText: 'Short flashcard sessions help you spot gaps quickly and return to questions that still need practice.',
    featureTwoTitle: 'A real rehearsal before the exam',
    featureTwoText: 'Take tests in the exam layout, with scoring, result history, and media matching the question database.',
    featureThreeTitle: 'One account for every category',
    featureThreeText: 'Choose your exam category and train the full material for motorcycles, passenger cars, trucks, or buses.',
    proofTitle: 'Start free, then unlock full access for pennies',
    proofText: 'Try studying without risk, and when you want unlimited practice, choose access from PLN 0.16 per day. Every exam category and the full question base are included.',
    profileCta: 'Go to profile',
    createAccountCta: 'Create account',
  },
  home: {
    title: 'Driving License Tests',
    signedIn: 'Signed in',
    username: 'Username',
    email: 'Email',
    noEmail: 'No email set',
    providers: 'Providers',
    emailVerified: 'Email verified',
    accountTier: 'Account tier',
    premiumUntil: 'Premium until',
    login: 'Login',
    register: 'Register',
    logout: 'Logout',
    emailOrUsername: 'Email or username',
    password: 'Password',
    createAccount: 'Create account',
    pleaseWait: 'Please wait...',
    authenticationFailed: 'Authentication failed.',
    or: 'or',
    googleNotConfigured: 'Google sign-in is not configured yet.',
    googleFailed: 'Google sign-in failed.',
    googleLoadFailed: 'Could not load Google sign-in.',
    usernamePlaceholder: 'Username',
    emailPlaceholder: 'Email',
    resendVerificationTitle: 'Resend verification email',
    accountEmail: 'Account email',
    resendEmail: 'Resend email',
    sending: 'Sending...',
    resetPasswordTitle: 'Reset password by email',
    sendResetEmail: 'Send reset email',
    changePasswordTitle: 'Change password',
    preferencesTitle: 'Study and exam preferences',
    savePreferences: 'Save preferences',
    preferencesSaved: 'Preferences saved.',
    preferencesFailed: 'Could not save preferences.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    saving: 'Saving...',
    changePassword: 'Change password',
    changePasswordFailed: 'Could not change password.',
    deleteAccountTitle: 'Delete account',
    deleteAccountHint: 'To confirm deletion, enter your username. Local accounts also require the current password.',
    usernameConfirmation: 'Username confirmation',
    deleteAccount: 'Delete account',
    deleting: 'Deleting...',
    deleteAccountFailed: 'Could not delete account.',
    activatePremiumCode: 'Activate Premium code',
    premiumCodePlaceholder: 'Enter Premium code',
    redeemCode: 'Redeem code',
    redeeming: 'Redeeming...',
    premiumActiveUntil: 'Premium active until',
    redeemFailed: 'Could not redeem Premium code.',
    generatePremiumCodes: 'Generate Premium codes',
    generateCodes: 'Generate codes',
    generating: 'Generating...',
    generateFailed: 'Could not generate Premium codes.',
    alreadySignedInTitle: 'You are signed in',
    alreadySignedInText: 'You can go to study mode or your profile.',
    studyModeCta: 'Study mode',
    profileCta: 'Profile',
    backToHome: 'Home',
    signInTitle: 'Sign in',
    createAccountTitle: 'Create account',
    authIntro: 'One account saves your exam category, language, study progress, and test history.',
    loginTab: 'Login',
    registerTab: 'Register',
    resendVerificationLink: 'Resend verification link',
    forgotPassword: 'Forgot password?',
    profileLoginTitle: 'User profile',
    profileLoginText: 'Sign in to manage your exam category, language, account, and Premium status.',
    premiumActive: 'Premium active until',
    emailVerifiedShort: 'Email verified',
    emailUnverifiedShort: 'Email not verified',
    loginMethod: 'Login',
  },
  verifyEmail: {
    title: 'Verify email',
    verifying: 'Verifying email...',
    incomplete: 'Verification link is incomplete.',
    failed: 'Could not verify email.',
    backToSignIn: 'Back to sign in',
  },
  resetPassword: {
    title: 'Reset password',
    incomplete: 'Reset link is incomplete.',
    failed: 'Could not reset password.',
    setNewPassword: 'Set new password',
    backToSignIn: 'Back to sign in',
  },
  notFound: {
    title: 'Page not found',
    text: 'The address you entered does not match any screen in this application. Go back to the main page or open one of the available flows.',
  },
  questionDemo: {
    pageTitle: 'Question Preview',
    pageDescription: 'Draw a random exam question and check the answer. Pick a scope to practice basic or specialist questions.',
    loginRequired: 'Log in on the home page to fetch a question from the backend.',
    loading: 'Checking session...',
    loadingCategories: 'Loading categories...',
    loadQuestion: 'Load another question',
    loadingQuestion: 'Loading question...',
    languageLabel: 'Language',
    scopeLabel: 'Scope',
    categoryLabel: 'Category',
    scopeBasic: 'Basic',
    scopeSpecialist: 'Specialist',
    fetchErrorFallback: 'Failed to fetch question.',
    categoriesErrorFallback: 'Failed to fetch categories.',
    publicPreviewNotice: 'Without an account you can view 10 sample questions stored locally in the app. We do not fetch them from the backend.',
    publicPreviewLoading: 'Loading the free set of 10 questions...',
    publicPreviewSignupButton: 'Create a free account',
    signupModalTitle: 'You reached the end of the free preview',
    signupModalText: 'Log in or create a free account to continue studying and unlock more questions.',
    signupModalCreateAccount: 'Create a free account',
    signupModalLogin: 'I already have an account',
    signupModalClose: 'Back to preview',
  },
  study: {
    title: 'Study Mode',
    description: 'Choose an exam category and a tag. Questions are shown as flashcards based on your progress.',
    loginRequired: 'Log in on the home page to start studying.',
    loading: 'Checking session...',
    loadingCategories: 'Loading categories and tags...',
    languageLabel: 'Language',
    categoryLabel: 'Exam category',
    tagLabel: 'Question tag',
    chooseScopeTitle: 'Choose what to study',
    allTags: 'All tags',
    start: 'Start studying',
    restart: 'Restart',
    loadingQuestions: 'Loading questions...',
    nextQuestion: 'Next',
    correct: 'Correct answer',
    incorrect: 'Incorrect answer',
    correctAnswer: 'Correct answer',
    progressCorrect: 'correct',
    progressNotCorrect: 'not yet',
    answered: 'Answered',
    correctCount: 'Correct',
    remaining: 'Queued',
    noQuestionLoaded: 'Choose a category and start studying.',
    fetchErrorFallback: 'Failed to fetch study questions.',
    categoriesErrorFallback: 'Failed to fetch categories or tags.',
    submitErrorFallback: 'Failed to save progress.',
    mediaUnavailable: 'This question has no media to display.',
    adTitle: 'Short ad break',
    adText: 'Free users see an ad after every 12 questions in Study Mode.',
    adContinue: 'Continue studying',
    adCountdown: 'Continue in',
  },
  exam: {
    title: 'Mock Exam',
    description: 'After you start, the full exam set for the selected category is fetched. You cannot return to previous questions.',
    loginRequired: 'Log in on the home page to start the exam.',
    loadingSession: 'Preparing exam...',
    loadingCategories: 'Loading categories...',
    languageLabel: 'Language',
    categoryLabel: 'Category',
    startExam: 'Start exam',
    startingExam: 'Loading questions...',
    nextQuestion: 'Next question',
    finishExam: 'Finish and check result',
    questionCounter: 'Question',
    globalTime: 'Total exam time',
    answeredCount: 'Answered',
    remainingCount: 'Remaining',
    resultTitle: 'Exam result',
    scoreLabel: 'Score',
    maxScoreLabel: 'Maximum score',
    passLabel: 'PASSED',
    failLabel: 'FAILED',
    passRule: 'You need at least 68 points to pass.',
    fetchErrorFallback: 'Failed to fetch exam set.',
    submitErrorFallback: 'Failed to check exam result.',
    submitInProgress: 'Checking result...',
    noExamLoaded: 'Select a category and click start to fetch the exam set.',
    categoriesErrorFallback: 'Failed to fetch categories.',
    resultAdTitle: 'See your result after the ad',
    resultAdText: 'Free users must see an ad before the exam result. Premium removes this step.',
    resultAdCountdown: 'Result available in',
    resultAdContinue: 'Show result',
    resultAdPremiumCta: 'Get Premium',
    summaryTitle: 'Question summary',
    basicQuestionsTitle: 'Basic questions',
    specialistQuestionsTitle: 'Specialist questions',
    correctAnswer: 'Correct answer',
    wrongAnswer: 'Wrong answer',
    userAnswer: 'Your answer',
    expectedAnswer: 'Correct answer',
    unanswered: 'No answer',
    testHistoryTitle: 'Test history',
    noTestHistory: 'No saved tests.',
  },
  ads: {
    googleSlotTitle: 'Google Ads slot',
    wideSkyscraperText: 'Placeholder for a 160 x 600 display slot.',
    drivingOfferTitle: 'Driving offer',
    secondSlotText: 'Placeholder for a second 160 x 600 display slot.',
    roadSafetyTitle: 'Road safety offer',
    drivingSchoolTitle: 'Driving school ad',
    examResultTitle: 'Exam result ad',
    mediumRectangleText: 'Placeholder for a Google Ads 300 x 250 display unit.',
  },
  premium: {
    plansError: 'Could not load plans. Try refreshing the page.',
    checkoutError: 'Could not start payment. Please try again.',
    loading: 'Loading...',
    lead: 'Unlock full access to exam history, ad-free study mode, and all question categories.',
    activeBadge: 'You have active Premium',
    activeUntil: 'until',
    loadingPlans: 'Loading plans...',
    popular: 'Most popular',
    featureHistory: 'Full exam history',
    featureNoAds: 'Ad-free study',
    featureAllCategories: 'All question categories',
    featureBestDailyPrice: 'Best price per day',
    redirecting: 'Redirecting...',
    buyNow: 'Buy now',
    loginToBuy: 'Sign in to buy',
    stripeHintPrefix: 'Payment handled by',
    stripeHintSuffix: 'One-time charge - no subscription.',
    successTitle: 'Payment complete!',
    successText: 'Your account has been activated. Premium is now active.',
    successHint: 'If Premium is not visible on your account yet, refresh the page in a moment - activation can take a few seconds.',
    backHome: 'Back to home',
    cancelTitle: 'Payment cancelled',
    cancelText: 'Nothing was charged to your card.',
    backToPlans: 'Back to plans',
  },
  scopeLabels: { PODSTAWOWY: 'Basic', SPECJALISTYCZNY: 'Specialist' },
  tagLabels: {
    'znaki-sygnaly': 'Road signs and signals',
    pierwszenstwo: 'Right of way',
    manewry: 'Manoeuvres and direction changes',
    ostroznosc: 'Care and caution',
    'predkosc-odstepy': 'Speed and following distance',
    'piesi-rowerzysci': 'Pedestrians, cyclists and crossings',
    'skrzyzowania-pasy': 'Intersections and lane traffic',
    'parkowanie-zatrzymanie': 'Parking and stopping',
    'warunki-jazdy': 'Driving conditions and visibility',
    'stan-kierowcy': 'Driver condition and safety',
    'pojazd-dokumenty': 'Vehicle, equipment and documents',
    'transport-ladunek': 'Passenger and cargo transport',
    inne: 'Other',
  },
};
